import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { useMotion } from '../hooks/useMotion';
import { api } from '../lib/api';
import { dayShape } from '../lib/dayShape';
import { formatDuration } from '../lib/time';
import { AnimatedNumber } from './AnimatedNumber';
import { ticketKey, ticketUrl } from './TicketText';

// The card only makes sense once the day has something in it, and only in the
// part of the day where looking back is the point.
const WRAP_FROM_HOUR = 15;

const todayBounds = (): { from: string; to: string } => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
};

const isToday = (iso: string | null): boolean => {
  if (!iso) {
    return false;
  }
  const value = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  const now = new Date();
  return (
    value.getFullYear() === now.getFullYear() &&
    value.getMonth() === now.getMonth() &&
    value.getDate() === now.getDate()
  );
};

/**
 * How the day turned out, from data the app already has: time per project,
 * the tickets that got closed, the longest unbroken stretch. A card on Domov,
 * never a pop-up — it waits to be read.
 */
export const DayWrapCard = () => {
  const { user } = useAuth();
  const { animates } = useMotion();
  const now = new Date();
  const enabled = Boolean(user) && now.getHours() >= WRAP_FROM_HOUR;

  const projectsQuery = useQuery({
    queryKey: ['projects', 'list', false],
    queryFn: () => api.projectsList(false),
    enabled,
    staleTime: 60_000,
  });
  const sessionsQuery = useQuery({
    queryKey: ['sessions', 'wrap', 'today'],
    queryFn: () => api.sessionsList({ ...todayBounds(), userId: user?.id, limit: 200 }),
    enabled,
    staleTime: 60_000,
  });
  const tasksQuery = useQuery({
    queryKey: ['tasks', 'wrap', user?.id ?? 0],
    queryFn: () => api.tasksList({ userId: user?.id, includeCompleted: 1 }),
    enabled,
    staleTime: 60_000,
  });

  const shape = useMemo(() => {
    const projects = projectsQuery.data?.items ?? [];
    return dayShape(sessionsQuery.data?.items ?? [], (projectId) =>
      projects.find((project) => project.id === projectId)?.color ?? '#8e8e93',
    );
  }, [projectsQuery.data?.items, sessionsQuery.data?.items]);

  const closedToday = useMemo(
    () =>
      (tasksQuery.data?.items ?? []).filter(
        (task) => task.completed && isToday(task.completedAt) && task.ticketNumber !== null,
      ),
    [tasksQuery.data?.items],
  );

  if (!enabled || shape.totalSeconds === 0) {
    return null;
  }

  return (
    <section className="panel overflow-hidden p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="label">Dnešok</p>
        <p className="text-[12px] text-slate-400 dark:text-slate-500">
          {shape.sessionCount === 1 ? '1 session' : `${shape.sessionCount} sessions`}
        </p>
      </div>

      <p className="mt-1 font-mono text-[30px] font-semibold tabular-nums tracking-tight">
        {animates ? (
          <AnimatedNumber value={shape.totalSeconds} format={(value) => formatDuration(value)} />
        ) : (
          formatDuration(shape.totalSeconds)
        )}
      </p>

      {/* Time per project as one bar — the shape of the day at a glance. */}
      <div className="mt-3 flex h-2 gap-1 overflow-hidden rounded-full">
        {shape.projects.map((slice) => (
          <span
            key={slice.projectId ?? 'none'}
            className={`rounded-full ${animates ? 'bar-grow origin-left' : ''}`}
            style={{
              flex: slice.seconds,
              backgroundColor: slice.color,
            }}
            title={`${slice.projectName} · ${formatDuration(slice.seconds)}`}
          />
        ))}
      </div>

      <dl className="mt-3 space-y-1.5">
        {shape.projects.slice(0, 3).map((slice) => (
          <div
            key={slice.projectId ?? 'none'}
            className="flex items-baseline justify-between gap-3 text-[13px]"
          >
            <dt className="flex min-w-0 items-center gap-2 text-slate-500 dark:text-slate-400">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: slice.color }}
                aria-hidden="true"
              />
              <span className="truncate">{slice.projectName}</span>
            </dt>
            <dd className="shrink-0 font-semibold tabular-nums">
              {formatDuration(slice.seconds)}
            </dd>
          </div>
        ))}
        {shape.longestBlockSeconds > 0 ? (
          <div className="flex items-baseline justify-between gap-3 text-[13px]">
            <dt className="text-slate-500 dark:text-slate-400">Najdlhší blok</dt>
            <dd className="font-semibold tabular-nums">
              {formatDuration(shape.longestBlockSeconds)}
            </dd>
          </div>
        ) : null}
      </dl>

      {closedToday.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t hairline pt-3">
          <span className="text-[12px] text-slate-400 dark:text-slate-500">Zavreté dnes</span>
          {closedToday.slice(0, 6).map((task) => (
            <Link
              key={task.id}
              to={
                task.projectId !== null && task.ticketNumber !== null
                  ? ticketUrl(task.projectId, task.ticketNumber)
                  : '/plan?tab=zoznam'
              }
              title={task.title}
              className="rounded-lg bg-emerald-500/10 px-2 py-1 font-mono text-[12px] font-semibold text-emerald-600 transition hover:bg-emerald-500/15 dark:text-emerald-400"
            >
              {task.ticketNumber !== null ? ticketKey(task.ticketNumber) : 'task'}
            </Link>
          ))}
          {closedToday.length > 6 ? (
            <span className="text-[12px] text-slate-400">+{closedToday.length - 6}</span>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};
